import {
  GoABlock,
  GoAButton,
  GoAButtonGroup,
  GoAFormItem,
  GoAModal,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
} from '@abgov/react-components';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import {
  defaultValidate,
  WizardButtonGroup,
} from '../wizard/wizard-button-group-component';
import { useEffect, useState } from 'react';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { updateCollision } from '../../state/slices/collisionSlice';
import { Collision } from '../../models/collision';
import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import { IsInReviewEditMode } from '../wizard/wizard-component';
import { setCollisionSubTypeUpdated } from '../../state/slices/configurationSlice';

const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export interface ParkedHitAndRunCoponentProps extends BaseWizardProps {}
export const ParkedHitAndRunComponent = ({
  navigationConfiguration,
}: ParkedHitAndRunCoponentProps) => {
  const dispatch = useAppDispatch();

  const defaultSelection = '0';
  const headerErrorText =
    'Choose an option that best applies to your situation';
  const errorText =
    'Please choose an option that best applies to your situation';
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [formError, setFormError] = useState('');
  const [selection, setSelection] = useState(defaultSelection);
  const [nextPage, setNextPage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalHeadingText, setModalHeadingText] = useState('heading');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [modalHeading, setModalHeading] = useState('');

  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const stateCollision = useAppSelector((state: RootState) => state.collision);

  useEffect(() => {
    if (stateCollision.collision) {
      if (stateCollision.collision.witnessOrCameraAvailable != undefined) {
        setSelection(
          stateCollision.collision.witnessOrCameraAvailable ? '2' : '1',
        );
      }
    }
  }, [stateCollision]);

  useEffect(() => {
    if (
      stateConfiguration.uiTriggers.collisionTypeUpdated ||
      stateConfiguration.uiTriggers.collisionSubTypeUpdated
    ) {
      setModalHeading('Your collision type changes have been saved');
    } else {
      setModalHeading('');
    }
  }, [
    stateConfiguration.uiTriggers.collisionTypeUpdated,
    stateConfiguration.uiTriggers.collisionSubTypeUpdated,
  ]);

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  const save = () => {
    const primaryEventType =
      stateConfiguration.configuration!.primaryEvent.find((x) => {
        return x.id == 48; // other/specify
      });

    let updatedCollision: Collision = { ...stateCollision.collision };

    if (primaryEventType) {
      if (
        navigationConfiguration.updateMetadata &&
        navigationConfiguration.stepId
      ) {
        const stepUpdated = navigationConfiguration.updateMetadata(
          updatedCollision,
          navigationConfiguration.stepId!,
          navigationConfiguration.stageCompleted
            ? navigationConfiguration.stageCompleted
            : false,
          !navigationConfiguration.stageCompleted,
        );

        if (stepUpdated && updatedCollision.metadata) {
          updatedCollision.metadata = {
            ...updatedCollision.metadata,
            formState: stepUpdated,
          };
        }
      }
      const prevPrimaryEventType = updatedCollision.primaryEvent;
      const prevObjectIdentificationType =
        updatedCollision.objectIdentification;
      const prevWitnessOrCameraAvailable =
        updatedCollision.witnessOrCameraAvailable;
      updatedCollision.primaryEvent = primaryEventType.id ?? 0;
      updatedCollision.objectIdentification = 0;
      updatedCollision.witnessOrCameraAvailable = selection === '2';
      updatedCollision.animalType = null;

      dispatch(updateCollision(updatedCollision));

      if (navigationConfiguration.save) {
        navigationConfiguration.save();
        if (
          IsInReviewEditMode() &&
          (prevPrimaryEventType != updatedCollision.primaryEvent ||
            prevObjectIdentificationType !=
              updatedCollision.objectIdentification ||
            prevWitnessOrCameraAvailable !=
              updatedCollision.witnessOrCameraAvailable)
        ) {
          dispatch(setCollisionSubTypeUpdated(true));
        }
      }
    }
  };

  const navigate = (page: string) => {
    if (IsInReviewEditMode()) {
      setShowReviewModal(true);
    } else {
      if (navigationConfiguration.navigate) {
        navigationConfiguration.navigate(page);
      }
    }
  };
  return (
    <div>
      <GoAModal
        calloutVariant="important"
        actions={
          <GoAButtonGroup alignment="end">
            <GoAButton
              onClick={() => {
                setShowReviewModal(false);
              }}
              type="tertiary"
            >
              Cancel
            </GoAButton>
            <GoAButton
              onClick={() => {
                setShowReviewModal(false);
                navigationConfiguration.navigate('#review');
              }}
              type="primary"
            >
              View report summary
            </GoAButton>
          </GoAButtonGroup>
        }
        heading={<div>{modalHeading}</div>}
        open={showReviewModal}
      >
        <div>
          Click 'View report summary to return to the report review page.
        </div>
      </GoAModal>
      <h1>Collision type</h1>
      The collision details section is the starting point for drivers to
      identify the type of collision they were involved in. In some cases you'll
      be prompted to add some additional details depending on your type of
      collision.
      <h2>My parked vehicle was involved in a hit and run</h2>
      <GoASpacer vSpacing="xs" />
      Do you have any information of the driver or the vehicle that committed
      the hit and run?
      <GoASpacer vSpacing="l" />
      <GoAFormItem error={formError}>
        <GoARadioGroup
          name="collisionTypeGroup"
          error={formError.length > 0}
          value={selection}
          onChange={(item, id) => {
            setSelection(id);
            setFormError('');
            dispatch(removeAllValidationErrorNotifications());

            if (id === '1') {
              setModalType('fraud');
              setModalHeadingText('Warning');

              setShowModal(true);
            }

            if (id === '2') {
              setModalType('camera');
              setModalHeadingText(
                'Witness or a camera provided some information',
              );

              setShowModal(true);
            }
          }}
        >
          <GoARadioItem
            value="1"
            label="No information is available"
          ></GoARadioItem>
          <GoARadioItem
            value="2"
            label="Witness or a camera provided some information"
          ></GoARadioItem>
        </GoARadioGroup>
      </GoAFormItem>
      <WizardButtonGroup
        nextPage={nextPage}
        disabled={disableSaveButton}
        validate={(): boolean => {
          return defaultValidate(
            selection,
            defaultSelection,
            errorText,
            setFormError,
            () => {
              dispatch(removeAllValidationErrorNotifications());
              dispatch(
                pushNotification({
                  body: headerErrorText,
                  notificationType: NotificationType.importantValidationError,
                }),
              );
            },
          );
        }}
        showViewSummaryButton={!IsInReviewEditMode()}
        goToReview={navigationConfiguration.goToReview}
        save={save}
        navigate={navigate}
      />
      <GoAModal
        calloutVariant="important"
        heading={modalHeadingText}
        open={showModal}
        actions={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ marginLeft: 'auto' }}>
              <GoAButton
                onClick={() => {
                  if (modalType == 'camera') {
                    setDisableSaveButton(true);
                  } else {
                    setDisableSaveButton(false);
                  }
                  setShowModal(false);
                }}
              >
                {modalType == 'camera' ? 'Exit' : 'I Understand'}
              </GoAButton>
            </div>
          </div>
        }
      >
        {modalType === 'camera' ? (
          <div>
            You cannot use Report My Collision online service to report a hit
            and run with available witness information or footage from a
            street/dash camera that could help identify the other driver or
            their vehicle. You must go to your local police station or reporting
            center to file a report. You can consult the below links for more
            information
            <GoASpacer vSpacing="xl" />
            <GoABlock direction="column" gap={'xs'}>
              <GoAButton
                type="tertiary"
                trailingIcon="open"
                onClick={() =>
                  window.open(
                    'https://www.calgary.ca/cps/calgary-police-service-district-offices.html',
                  )
                }
              >
                Calgary Police Service
              </GoAButton>
              <GoAButton
                type="tertiary"
                trailingIcon="open"
                onClick={() =>
                  window.open(
                    'https://www.edmontonpolice.ca/ContactEPS/EPSBranchLocations.aspx',
                  )
                }
              >
                Edmonton Police Service
              </GoAButton>
              <GoAButton
                type="tertiary"
                trailingIcon="open"
                onClick={() =>
                  window.open('https://www.rcmp-grc.gc.ca/detach/en/find/AB')
                }
              >
                RCMP
              </GoAButton>
            </GoABlock>
            <GoASpacer vSpacing="xl" />
          </div>
        ) : (
          '  '
        )}
        {modalType === 'fraud' ? (
          <div>
            The submission of false, misleading, or incorrect information is
            prohibited, and may result in delays in processing, rejection of
            your submission, termination of your Report My Collision account,
            and potential consequences as identified in the Traffic Safety Act.
            Please refer to the act for more information.
            <GoASpacer vSpacing="xl" />
            <GoAButton
              type="tertiary"
              trailingIcon="open"
              onClick={() =>
                window.open('https://open.alberta.ca/publications/t06')
              }
            >
              Traffic Safety Act
            </GoAButton>
          </div>
        ) : (
          '  '
        )}
      </GoAModal>
    </div>
  );
};
